import * as tslib_1 from "tslib";
import { ElementRef, EventEmitter, ViewContainerRef } from '@angular/core';
import { version } from '../../package.json';
import { util } from '../classes/util';
import { environment } from '../environment';
import { ESRI } from './../esri-loader-util';
var GoogleAnalyticsUrl = 'https://www.google-analytics.com/analytics.js';
var MapCoreGoogleAnalyticsTrackingId = 'UA-65152108-6';
var componentIdCounter = 1;
/** Base of MapCore components */
var BaseComponent = /** @class */ (function () {
    function BaseComponent(host, startReady) {
        var _this = this;
        /** Promise to notify when a component is ready */
        this.ready = new Promise(function (resolve, reject) {
            _this._resolveReady = resolve;
            _this._rejectReady = reject;
        });
        this.registerAttributeEventSubscriptions = new Map();
        this.componentId = componentIdCounter++;
        if (startReady) {
            this.isReady();
        }
        if (host && host.element && host.element.nativeElement) {
            this.hostElement = host.element.nativeElement;
        }
        if (this.includeEsriCss) {
            this.setupEsriStylesheet();
        }
        if (this.includeOverrideCss) {
            this.setupStylesheetOverride();
        }
        if (this.includeGoogleAnalytics) {
            this.setupGoogleAnalytics();
        }
        this.unregisterWithMcScope = this.unregisterWithMcScope.bind(this);
    }
    Object.defineProperty(BaseComponent.prototype, "includeEsriCss", {
        get: function () { return true; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseComponent.prototype, "includeOverrideCss", {
        get: function () { return true; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseComponent.prototype, "includeGoogleAnalytics", {
        get: function () { return true; },
        enumerable: true,
        configurable: true
    });
    BaseComponent.prototype.isReady = function (also) { this._resolveReady(also || true); };
    BaseComponent.prototype.readyProblem = function (reason) { this._rejectReady(reason); };
    BaseComponent.prototype.getParentScope = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var timeoutMilliseconds, parentScope;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(this.parentScope === undefined)) return [3 /*break*/, 3];
                        return [4 /*yield*/, this.waitForHostElement()];
                    case 1:
                        _a.sent();
                        timeoutMilliseconds = 50;
                        return [4 /*yield*/, util.retryUntilValue(function () {
                                return util.getClosest(_this.hostElement, 'mc-scope');
                            }, timeoutMilliseconds)];
                    case 2:
                        parentScope = _a.sent();
                        if (parentScope) {
                            this.parentScope = parentScope;
                        }
                        else {
                            this.parentScope = null;
                        }
                        _a.label = 3;
                    case 3: return [2 /*return*/, this.parentScope];
                }
            });
        });
    };
    /**
     * Returns a reference to the angular component (since angular doesn't seem to provide a way to do this?).
     * This is used for things like getClosestAncestor so it will always return the component instead of the html element.
     */
    BaseComponent.prototype.getComponentReference = function () {
        return this;
    };
    /**
     * Allows child components to register themselves with mc-scope so they are discoverable by other components.
     * This is mainly called by maps, but mc-scope is used to resolve maps for many other components
     */
    BaseComponent.prototype.registerWithMcScope = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var mcScope;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getParentScope()];
                    case 1:
                        mcScope = _a.sent();
                        if (mcScope) {
                            mcScope.registerComponent(this);
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    BaseComponent.prototype.unregisterWithMcScope = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var mcScope;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getParentScope()];
                    case 1:
                        mcScope = _a.sent();
                        if (mcScope) {
                            mcScope.unregisterComponent(this);
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Given a tag, look for an attribute with `id` at the end and return its value, or null if not found.
     * (e.g `mc-map` would result in looking for `mc-map-id` and `mcMapId` )
     */
    BaseComponent.prototype.getAttributeValue = function (snakeCaseAttributeName) {
        var e_1, _a;
        if (this.hostElement.attributes) {
            var attributeNames = [snakeCaseAttributeName, util.snakeToCamelCase(snakeCaseAttributeName)];
            try {
                for (var attributeNames_1 = tslib_1.__values(attributeNames), attributeNames_1_1 = attributeNames_1.next(); !attributeNames_1_1.done; attributeNames_1_1 = attributeNames_1.next()) {
                    var attributeName = attributeNames_1_1.value;
                    var targetAttribute = this.hostElement.attributes.getNamedItem(attributeName);
                    if (targetAttribute) {
                        var attributeValue = targetAttribute.value;
                        return attributeValue;
                    }
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (attributeNames_1_1 && !attributeNames_1_1.done && (_a = attributeNames_1.return)) _a.call(attributeNames_1);
                }
                finally { if (e_1) throw e_1.error; }
            }
        }
        return;
    };
    /**
     * Certain lifecycle calls are run before the host element is ready, so await this promise
     * to be notified when the hostElement is ready
     */
    BaseComponent.prototype.waitForHostElement = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var sleepMs, maxIterations, i;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        sleepMs = 15;
                        maxIterations = 20;
                        i = 0;
                        _a.label = 1;
                    case 1:
                        if (!(i < maxIterations)) return [3 /*break*/, 4];
                        if (this.hostElement) {
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, util.sleep(sleepMs)];
                    case 2:
                        _a.sent();
                        _a.label = 3;
                    case 3:
                        i++;
                        return [3 /*break*/, 1];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Get the closest parent with the specified tag. If a parent can't be found, ask the mc-scope
     * @param selector - the tag name of the of the ancestor component you want (i.e. "mc-map", "mc-legend", etc...)
     * @param id - the id of the ancestor component you want. This is optional, and is only used when asking mc-scope
     */
    BaseComponent.prototype.getClosestAncestor = function (tagName) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var id, element, scope, component, e_2, targetIdText, hostIdText;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        id = this.getAttributeValue(tagName + "-id");
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 9, , 10]);
                        return [4 /*yield*/, this.waitForHostElement()];
                    case 2:
                        _a.sent();
                        // wait for host element to be initialized
                        if (!this.hostElement) {
                            return [2 /*return*/, null];
                        }
                        if (!!id) return [3 /*break*/, 4];
                        element = util.getClosest(this.hostElement, tagName);
                        if (!element) return [3 /*break*/, 4];
                        return [4 /*yield*/, this.waitForShadowRoot(element)];
                    case 3:
                        _a.sent();
                        return [2 /*return*/, element.getComponentReference()];
                    case 4: return [4 /*yield*/, this.getScope()];
                    case 5:
                        scope = _a.sent();
                        if (!scope) return [3 /*break*/, 7];
                        return [4 /*yield*/, scope.getComponent(tagName, id)];
                    case 6:
                        component = _a.sent();
                        if (component) {
                            return [2 /*return*/, component];
                        }
                        else {
                            throw new Error('Could not find the desired element in ancestor <mc-scope>');
                        }
                        return [3 /*break*/, 8];
                    case 7: throw new Error('Could not find ancestor <mc-scope>');
                    case 8: return [3 /*break*/, 10];
                    case 9:
                        e_2 = _a.sent();
                        targetIdText = id ? " with id \"" + id + "\"" : '';
                        hostIdText = this.hostElement.id ? " with id \"" + this.hostElement.id + "\"" : '';
                        e_2 = "Error loading closest ancestor <" + tagName + targetIdText + "> for <" + this.hostElement.tagName + ">" + hostIdText + ": " + e_2;
                        throw e_2;
                    case 10: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Get the assigned component in Slot
     * @param selector - the tag name of the of the ancestor component you want (i.e. "mc-map", "mc-legend", etc...)
     * @param slotRef - the Slot Element reference
     */
    BaseComponent.prototype.getSlotAssignedComponent = function (slotRef, tagName) {
        if (slotRef && slotRef.nativeElement.assignedNodes().length > 0) {
            var element = slotRef.nativeElement.assignedNodes()[0];
            if (tagName && element.nodeName.toLowerCase() === tagName) {
                return element;
            }
            {
                return element;
            }
        }
        else {
            throw new Error('No component assigned to the slot');
        }
    };
    /**
     * Get the parent mc-scope element. Will retry for up to half a second before
     * giving up and throwing an exception
     */
    BaseComponent.prototype.getScope = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var maxDurationMilliseconds;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                maxDurationMilliseconds = 400;
                return [2 /*return*/, util.retryUntilValue(function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                        var scope;
                        return tslib_1.__generator(this, function (_a) {
                            switch (_a.label) {
                                case 0:
                                    scope = util.getClosest(this.hostElement, 'mc-scope');
                                    if (!scope) return [3 /*break*/, 2];
                                    // mc-scope definitely has shadow-root
                                    return [4 /*yield*/, this.waitForShadowRoot(scope)];
                                case 1:
                                    // mc-scope definitely has shadow-root
                                    _a.sent();
                                    return [2 /*return*/, scope];
                                case 2: return [2 /*return*/];
                            }
                        });
                    }); }, maxDurationMilliseconds)];
            });
        });
    };
    BaseComponent.prototype.waitForShadowRoot = function (element) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var sleepMS, maxChecks, currentChecks;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        sleepMS = 50;
                        maxChecks = 20;
                        currentChecks = 0;
                        _a.label = 1;
                    case 1:
                        if (!(!element.shadowRoot && currentChecks < maxChecks)) return [3 /*break*/, 3];
                        currentChecks++;
                        return [4 /*yield*/, util.sleep(sleepMS)];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 1];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    BaseComponent.prototype.setupStylesheetOverride = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var stylesheetLink;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(this.hostElement && window.overrideStylesheet)) return [3 /*break*/, 2];
                        stylesheetLink = document.createElement('link');
                        stylesheetLink.rel = 'stylesheet';
                        stylesheetLink.href = window.overrideStylesheet;
                        return [4 /*yield*/, this.addStyleElement(stylesheetLink, true)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    BaseComponent.prototype.setupEsriStylesheet = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var stylesheet;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.hostElement) return [3 /*break*/, 2];
                        stylesheet = document.createElement('style');
                        stylesheet.innerText = "@import url('" + ESRI.esriStylesUrl + "');";
                        return [4 /*yield*/, this.addStyleElement(stylesheet)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    BaseComponent.prototype.addStyleElement = function (el, last) {
        if (last === void 0) { last = false; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var shadowRoot, styleBlocks;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.waitForShadowRoot(this.hostElement)];
                    case 1:
                        _a.sent();
                        shadowRoot = this.hostElement.shadowRoot;
                        if (shadowRoot) {
                            if (shadowRoot.children.length) {
                                styleBlocks = shadowRoot.querySelectorAll('style');
                                if (styleBlocks.length && last) {
                                    styleBlocks[styleBlocks.length - 1].after(el);
                                }
                                else {
                                    shadowRoot.insertBefore(el, shadowRoot.firstChild);
                                }
                            }
                            else {
                                shadowRoot.append(el);
                            }
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    BaseComponent.prototype.setupMapViewRef = function (mapViewRef) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var parent_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!!mapViewRef) return [3 /*break*/, 5];
                        return [4 /*yield*/, this.getClosestAncestor('mc-map')];
                    case 1:
                        parent_1 = _a.sent();
                        if (!parent_1) return [3 /*break*/, 3];
                        return [4 /*yield*/, parent_1.ready];
                    case 2:
                        _a.sent();
                        return [2 /*return*/, parent_1.mapView];
                    case 3:
                        this.readyProblem('Map View Reference not supplied and reference from parent element could not be retrieved.');
                        _a.label = 4;
                    case 4: return [3 /*break*/, 6];
                    case 5: return [2 /*return*/, mapViewRef];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    BaseComponent.prototype.trackerName = function (trackingIdIndex) { return environment.appName + "(MapCore_" + version + ")_" + trackingIdIndex; };
    Object.defineProperty(BaseComponent.prototype, "trackingIds", {
        get: function () {
            return environment.googleAnalyticsTrackingIds && environment.googleAnalyticsTrackingIds.length
                ? tslib_1.__spread([MapCoreGoogleAnalyticsTrackingId], environment.googleAnalyticsTrackingIds) : [MapCoreGoogleAnalyticsTrackingId];
        },
        enumerable: true,
        configurable: true
    });
    BaseComponent.prototype.runPerTrackingId = function (toRun) {
        for (var trackingIdIndex = 0; trackingIdIndex < this.trackingIds.length; trackingIdIndex++) {
            toRun(this.trackingIds[trackingIdIndex], trackingIdIndex);
        }
    };
    BaseComponent.prototype.setupGoogleAnalytics = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!environment.production) return [3 /*break*/, 2];
                        window.GoogleAnalyticsObject = 'mc2ga';
                        return [4 /*yield*/, util.loadScript(GoogleAnalyticsUrl, 'MC-GoogleAnalytics', function () { return window.mc2ga; })];
                    case 1:
                        _a.sent();
                        window.GoogleAnalyticsObject = undefined;
                        this.runPerTrackingId(function (trackingId, trackingIdIndex) {
                            var tracker = window.mc2ga.getByName(_this.trackerName(trackingIdIndex));
                            if (!tracker) {
                                tracker = window.mc2ga.create(trackingId, 'auto', _this.trackerName(trackingIdIndex));
                                tracker.send('pageview');
                            }
                            tracker.send('event', {
                                eventCategory: _this.hostElement.tagName,
                                eventAction: 'Component Loaded',
                                nonInteraction: true
                            });
                        });
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Sends infomation to Google Analytics. Should only be used if extra data is needed, as a component loading is already sent.
     * @param eventAction - The event that occured.
     * @param nonInteraction - Should only be set to false if the event is triggered by user interaction.
     * @param eventCategory - The category of the event. Only set this if you want the event to show up some place specific. Defaults to the component's tag.
     */
    BaseComponent.prototype.sendAnalyticsEvent = function (eventAction, nonInteraction, eventCategory) {
        if (nonInteraction === void 0) { nonInteraction = true; }
        if (eventCategory === void 0) { eventCategory = null; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!!window.mc2ga) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.setupGoogleAnalytics()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        if (window.mc2ga) {
                            if (!eventCategory) {
                                eventCategory = this.hostElement.tagName;
                            }
                            this.runPerTrackingId(function (_trackingId, trackingIdIndex) {
                                var tracker = window.mc2ga.getByName(_this.trackerName(trackingIdIndex));
                                if (tracker) {
                                    tracker.send('event', {
                                        eventCategory: eventCategory,
                                        eventAction: eventAction,
                                        nonInteraction: nonInteraction
                                    });
                                }
                            });
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Take a string (like "a.b.c"), execute that function against window.
     * For example, window.a.b.c(value)
     */
    BaseComponent.prototype.callOnWindow = function (path) {
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        var e_3, _a;
        var parts = path.split('.');
        var obj;
        obj = window;
        try {
            for (var parts_1 = tslib_1.__values(parts), parts_1_1 = parts_1.next(); !parts_1_1.done; parts_1_1 = parts_1.next()) {
                var part = parts_1_1.value;
                obj = obj[part];
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (parts_1_1 && !parts_1_1.done && (_a = parts_1.return)) _a.call(parts_1);
            }
            finally { if (e_3) throw e_3.error; }
        }
        obj.call.apply(obj, tslib_1.__spread([window], args));
    };
    BaseComponent.prototype.registerEvent = function (attributeName, emitter) {
        var _this = this;
        // clean up old subscriptions if the user changes this value
        if (this.registerAttributeEventSubscriptions.has(emitter)) {
            try {
                this.registerAttributeEventSubscriptions.get(emitter).unsubscribe();
            }
            catch (e) { }
        }
        // force attribute name to lower case for consistency
        attributeName = attributeName.toLowerCase();
        // subscribe to this event emitter
        var subscription = emitter.subscribe(function (evt) {
            // if there is an attribute with the specified name, call whatever the function it references
            var targetAttribute = _this.hostElement.attributes.getNamedItem(attributeName);
            if (targetAttribute) {
                var attributeValue = targetAttribute.value;
                _this.callOnWindow(attributeValue, evt);
            }
            if (_this.hostElement[attributeName]) {
                _this.hostElement[attributeName](evt);
            }
        });
        this.registerAttributeEventSubscriptions.set(emitter, subscription);
    };
    return BaseComponent;
}());
export { BaseComponent };
